import React, { useMemo, useState } from 'react';
import { getScoreColor, getSWOTCounts } from '../utils/evaluationScoreUtils';
import '../styles/components/EvaluationSummary.css';

interface Question {
  id: number;
  points: number;
  form_question: {
    points: number;
  };
}

interface SWOTItem {
  id: number;
  finding: string;
  swot_type_id: number;
  section_id?: number;
}

interface EvaluationSummaryProps {
  evaluationScore: number;
  maxScore: number;
  questions: Question[];
  swotItems: SWOTItem[];
  showSummary?: boolean;
  onNavigateToQuestion: (questionId: number) => void;
  onNavigateToSWOT: (swotId: number) => void;
}

const EvaluationSummary: React.FC<EvaluationSummaryProps> = ({
  evaluationScore,
  maxScore,
  questions,
  swotItems,
  showSummary = false,
  onNavigateToQuestion,
  onNavigateToSWOT
}) => {
  // Add state for tracking current indices
  const [perfectIndex, setPerfectIndex] = useState(0);
  const [goodIndex, setGoodIndex] = useState(0);
  const [poorIndex, setPoorIndex] = useState(0);
  const [strengthsIndex, setStrengthsIndex] = useState(0);
  const [trainingIndex, setTrainingIndex] = useState(0);
  
  // Calculate question statistics
  const questionStats = useMemo(() => {
    const perfect = questions.filter(q => q.points === q.form_question.points && q.points > 0);
    const good = questions.filter(q => {
      const percentage = (q.points / q.form_question.points) * 100;
      return percentage >= 70 && percentage < 100 && q.points > 0;
    });
    const poor = questions.filter(q => {
      const percentage = (q.points / q.form_question.points) * 100;
      return percentage < 70 && q.points >= 0; // Exclude N/A questions (points = -1)
    });

    return {
      perfect,
      good,
      poor
    };
  }, [questions]);

  // Calculate SWOT statistics
  const swotStats = useMemo(() => {
    const strengths = swotItems.filter(item => item.swot_type_id === 1); // Assuming 1 = Strengths
    const trainingNeeds = swotItems.filter(item => item.swot_type_id === 2); // Assuming 2 = Training Needs

    return {
      strengths,
      trainingNeeds
    };
  }, [swotItems]);

  // Navigation functions for questions
  const navigateQuestion = (category: 'perfect' | 'good' | 'poor', direction: 'next' | 'prev') => {
    const items = questionStats[category];
    if (!items || items.length === 0) return;
    
    // Get current index setter based on category
    const setIndex = {
      perfect: setPerfectIndex,
      good: setGoodIndex,
      poor: setPoorIndex
    }[category];
    
    // Get current index based on category
    const currentIndex = {
      perfect: perfectIndex,
      good: goodIndex,
      poor: poorIndex
    }[category];

    // Calculate new index
    let newIndex = currentIndex;
    if (direction === 'next') {
      newIndex = (newIndex + 1) % items.length;
    } else {
      newIndex = (newIndex - 1 + items.length) % items.length;
    }

    // Update state and navigate
    setIndex(newIndex);
    onNavigateToQuestion(items[newIndex].id);
  };

  // Navigation functions for SWOT
  const navigateSWOT = (category: 'strengths' | 'trainingNeeds', direction: 'next' | 'prev') => {
    const items = swotStats[category];
    if (!items || items.length === 0) return;
    
    // Get current index setter based on category
    const setIndex = {
      strengths: setStrengthsIndex,
      trainingNeeds: setTrainingIndex
    }[category];
    
    // Get current index based on category
    const currentIndex = {
      strengths: strengthsIndex,
      trainingNeeds: trainingIndex
    }[category];

    // Calculate new index
    let newIndex = currentIndex;
    if (direction === 'next') {
      newIndex = (newIndex + 1) % items.length;
    } else {
      newIndex = (newIndex - 1 + items.length) % items.length;
    }

    // Update state and navigate
    setIndex(newIndex);
    onNavigateToSWOT(items[newIndex].id);
  };

  // Calculate score percentage
  const scorePercentage = ((evaluationScore / maxScore) * 100).toFixed(0);



  return (
    // <div className="evaluation-summary">
      <div className="summary-header">
        <div className="summary-header-left">
          <h3 className="call-score-header">
            Total Score 
          </h3>      
        </div>
        <div className="summary-header-right">
          <div className="summary-swot-counts">
            <span className="swot-label">SWOT</span>
            {(() => {
              const counts = getSWOTCounts(swotItems);
              return (
                <>
                  {counts.strength > 0 && (
                    <span className="swot-count-badge theme-success">
                      {counts.strength}
                    </span>
                  )}
                  {counts.opportunity > 0 && (
                    <span className="swot-count-badge theme-warning">
                      {counts.opportunity}
                    </span>
                  )}
                  {counts.weakness > 0 && (
                    <span className="swot-count-badge theme-danger">
                      {counts.weakness}
                    </span>
                  )}
                  {counts.threat > 0 && (
                    <span className="swot-count-badge theme-danger">
                      {counts.threat}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          <div className={`call-score ${getScoreColor(evaluationScore, maxScore)}`}>
            <span>{evaluationScore}/{maxScore}</span>
          </div>

        </div>
      {/* </div> */}

      {showSummary && (
        <div className="summary-content">
          <div className="summary-questions">
            <h3>Question Scores</h3>
            
            <div className="summary-item perfect">
              <div className="item-content">
                <span className="item-label">100%</span>
                <span className="item-count">
                  {questionStats.perfect.length}/{questions.length}
                </span>
              </div>
              <div className="item-navigation">
                <button 
                  className="nav-button" 
                  onClick={() => navigateQuestion('perfect', 'prev')}
                  disabled={questionStats.perfect.length === 0}
                >
                  <div className="arrow-up"></div>
                </button>
                <button 
                  className="nav-button" 
                  onClick={() => navigateQuestion('perfect', 'next')}
                  disabled={questionStats.perfect.length === 0}
                >
                  <div className="arrow-down"></div>
                </button>
              </div>
            </div>

            <div className="summary-item good">
              <div className="item-content">
                <span className="item-label">70-99%</span>
                <span className="item-count">
                  {questionStats.good.length}/{questions.length}
                </span>
              </div>
              <div className="item-navigation">
                <button 
                  className="nav-button" 
                  onClick={() => navigateQuestion('good', 'prev')}
                  disabled={questionStats.good.length === 0}
                >
                  <div className="arrow-up"></div>
                </button>
                <button 
                  className="nav-button" 
                  onClick={() => navigateQuestion('good', 'next')}
                  disabled={questionStats.good.length === 0}
                >
                  <div className="arrow-down"></div>
                </button>
              </div>
            </div>

            <div className="summary-item poor">
              <div className="item-content">
                <span className="item-label">&lt;70%</span>
                <span className="item-count">
                  {questionStats.poor.length}/{questions.length}
                </span>
              </div>
              <div className="item-navigation">
                <button 
                  className="nav-button" 
                  onClick={() => navigateQuestion('poor', 'prev')}
                  disabled={questionStats.poor.length === 0}
                >
                  <div className="arrow-up"></div>
                </button>
                <button 
                  className="nav-button" 
                  onClick={() => navigateQuestion('poor', 'next')}
                  disabled={questionStats.poor.length === 0}
                >
                  <div className="arrow-down"></div>
                </button>
              </div>
            </div>
          </div>

          <div className="summary-insights">
            <h3>Insights Summary</h3>
            
            <div className="summary-item strengths">
              <div className="item-content">
                <span className="item-label">Strengths</span>
                <span className="item-count">{swotStats.strengths.length}</span>
              </div>
              <div className="item-navigation">
                <button 
                  className="nav-button" 
                  onClick={() => navigateSWOT('strengths', 'prev')}
                  disabled={swotStats.strengths.length === 0}
                >
                  <div className="arrow-up"></div>
                </button>
                <button 
                  className="nav-button" 
                  onClick={() => navigateSWOT('strengths', 'next')}
                  disabled={swotStats.strengths.length === 0}
                >
                  <div className="arrow-down"></div>
                </button>
              </div>
            </div>

            <div className="summary-item training-needs">
              <div className="item-content">
                <span className="item-label">Training</span>
                <span className="item-count">{swotStats.trainingNeeds.length}</span>
              </div>
              <div className="item-navigation">
                <button 
                  className="nav-button" 
                  onClick={() => navigateSWOT('trainingNeeds', 'prev')}
                  disabled={swotStats.trainingNeeds.length === 0}
                >
                  <div className="arrow-up"></div>
                </button>
                <button 
                  className="nav-button" 
                  onClick={() => navigateSWOT('trainingNeeds', 'next')}
                  disabled={swotStats.trainingNeeds.length === 0}
                >
                  <div className="arrow-down"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationSummary; 